import { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { useCollections } from "~/state/collections";
import { usePlants } from "~/state/plants";
import { spacing } from "~/styles/theme";

export default function AllPlantsScreen() {
  const router = useRouter();
  const collections = useCollections();
  const plants = usePlants();
  const [term, setTerm] = useState("");
  const [collectionId, setCollectionId] = useState("");

  const collectionOptions = useMemo(
    () => [
      { value: "", label: "All collections" },
      ...collections.map((collection) => ({
        value: collection.id,
        label: collection.name,
      })),
    ],
    [collections],
  );

  const collectionNames = useMemo(
    () =>
      new Map(
        collections.map((collection) => [collection.id, collection.name]),
      ),
    [collections],
  );

  // Filtering happens against the local store, so search keeps working offline.
  const filtered = plants.filter((plant) => {
    if (collectionId && plant.collection_id !== collectionId) return false;
    const query = term.trim().toLowerCase();
    if (!query) return true;
    return [plant.display_name, plant.nickname, plant.notes, ...plant.tags]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });

  const struggling = plants.filter(
    (plant) => plant.status === "struggling",
  ).length;

  return (
    <ProtectedScreen
      title="All plants"
      subtitle="Everything you've catalogued, across every collection."
    >
      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard label="Plants" value={String(plants.length)} />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Collections" value={String(collections.length)} />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Needing attention" value={String(struggling)} />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader title="Find a plant" />
        <View style={stylesheet.filters}>
          <View style={stylesheet.filter}>
            <AppText size="xs" weight="semibold">
              Search
            </AppText>
            <TextField
              accessibilityLabel="Search your plants"
              value={term}
              placeholder="Monstera, the one by the door, kitchen..."
              onChangeText={setTerm}
            />
          </View>
          <View style={stylesheet.filter}>
            <AppText size="xs" weight="semibold">
              Collection
            </AppText>
            <SearchableSelect
              title="Filter by collection"
              accessibilityLabel="Collection"
              value={collectionId}
              options={collectionOptions}
              placeholder="All collections"
              onValueChange={setCollectionId}
            />
          </View>
        </View>

        {filtered.length === 0 ? (
          <EmptyState
            title={plants.length === 0 ? "No plants yet" : "No matches"}
            description={
              plants.length === 0
                ? "Open a collection and add your first plant."
                : "Try a different search or clear the collection filter."
            }
          />
        ) : (
          <Stack gap="sm">
            {filtered.map((plant) => (
              <ListRow
                key={plant.id}
                title={plant.display_name}
                description={plant.notes ?? undefined}
                onPress={() => router.push(`/plants/${plant.id}` as never)}
                meta={
                  <>
                    <Badge tone="neutral">
                      {collectionNames.get(plant.collection_id) ?? "Collection"}
                    </Badge>
                    <Badge tone="neutral">{plant.status}</Badge>
                    {plant.quantity > 1 ? (
                      <Badge tone="neutral">×{plant.quantity}</Badge>
                    ) : null}
                  </>
                }
              />
            ))}
          </Stack>
        )}
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 100, flex: 1 },
  filters: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  filter: { minWidth: 220, flex: 1, gap: spacing.xs },
});
